import React from 'react'
import PropTypes from 'prop-types'
import { GET_LIST, useClient } from '@peracto/client'
import AsyncSelect from 'react-select/async'
import debounce from 'debounce-promise'
import { Field, getIn } from 'formik'
import { Form, FormGroup, Group, Checkbox } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'

export const MODE_EDIT = 'edit'

const getError = (form, field, label) => {
    let error = getIn(form.errors, field.name)

    if (error) {
        error = error.replace(field.name, label)
    }
    return error
}

const ElementAreasForm = ({ mode = MODE_EDIT, ...props }) => {
    const { client } = useClient()
    const config = useConfig()
    const { content } = config.get('features', {})
    const allowDisableContentElementAreas = content?.allowDisableContentElementAreas || false

    const fetchContent = async inputValue => {
        const { data } = await client(GET_LIST, 'contents', {
            id: 'contents',
            filter: {
                title: inputValue,
                type: 'element',
            },
        })

        const values = data.map(content => ({
            label: content.title,
            value: content.id,
        }))

        return values
    }

    const debouncedFetchContent = debounce(fetchContent, 200)

    return (
        <Form autoComplete="off" {...props}>
            <Group key="core-data" id="core-data" name="Core Data">
                <Field name="elementArea">
                    {({ field, form }) => {
                        const label = 'Element Area'
                        const error = getError(form, field, label)

                        let value = { label: 'None', value: null }

                        if (field?.value?.id) {
                            value = { label: field.value.name || '-', value: field.value.id }
                        }

                        return (
                            <FormGroup
                                name={field.name}
                                label={label}
                                help="Select to assign an Element to this Area"
                                error={error}
                            >
                                <div data-testid="element-area-dropdown">
                                    <AsyncSelect
                                        className="!w-full"
                                        classNamePrefix="list"
                                        loadOptions={input => debouncedFetchContent(input)}
                                        isSearchable={true}
                                        defaultOptions={true}
                                        onChange={option => {
                                            form.setFieldValue(field.name, {
                                                name: option.label,
                                                id: option.value,
                                            })
                                        }}
                                        value={value}
                                        placeholder="Search for a Content Element..."
                                        noOptionsMessage={({ inputValue }) => {
                                            if (inputValue.length > 0) {
                                                return `No results found for '${inputValue}'.`
                                            } else {
                                                return 'Enter text to begin searching.'
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        )
                    }}
                </Field>

                {allowDisableContentElementAreas && (
                    <Field name="enabled">
                        {({ field, form }) => {
                            return (
                                <FormGroup
                                    name={field.name}
                                    label="Enabled"
                                    help="Enable or disable this Element Area"
                                >
                                    <Checkbox
                                        name="enabled"
                                        checked={field.value}
                                        onChange={e => {
                                            form.setFieldValue('enabled', e.target.checked)
                                        }}
                                        label="Enabled"
                                    />
                                </FormGroup>
                            )
                        }}
                    </Field>
                )}
            </Group>
        </Form>
    )
}

ElementAreasForm.displayName = 'ElementAreasForm'

ElementAreasForm.propTypes = {
    values: PropTypes.object,
    mode: PropTypes.oneOf([MODE_EDIT]),
    schema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default ElementAreasForm
